import React, { useMemo, useEffect, useState } from "react";
import ReactFlow, {
  Controls,
  ConnectionLineType,
  ReactFlowProvider,
} from "reactflow";
import { useDispatch } from "react-redux";

import {
  flowBuildersActions,
  useFlowBuildersSelector,
} from "../../redux/reducers/flowBuilderReducer";
import _ from "lodash";
import "reactflow/dist/style.css";
import { getLayoutedElements } from "./utils";
import QuestionCard from "./components/question";
import ChatCard from "./components/chat";
import AnswerCard from "./components/answer";
import ButtonCard from "./components/button";
import CustomEdge from "./components/edge";
import CustomNodeCard from "./components/customNode";
import { FLOW_CHART_NODE_TYPES } from "./components/constants";
import env from "env";

const proOptions = {
  account: env.REACT_APP_FLOW_LICENSE,
  hideAttribution: true,
};

const edgeTypes = {
  custom: CustomEdge,
};

const LayoutFlow = ({
  name = "chat",
  initialData,
  onEdit,
  style = {},
  nodeProps,
  childNodeProps,
}) => {
  const flowBuilderData = useFlowBuildersSelector(name);
  let flowBuilder = name ? _.cloneDeep(flowBuilderData) : false;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      flowBuildersActions.initialize({
        name,
        nodes: initialData.nodes,
        edges: initialData.edges,
      })
    );
  }, []);

  const [reactFlowInstance, setReactFlowInstance] = useState();
  const [mustFitView, setMustFitView] = useState(false);
  const [initialized, setInitialized] = useState(false);

  //this useMemo is required based on documentation to avoid issues with react-flow
  //also the dependency array must be empty
  //NOTE: this causes issues with redux state. If you need to use the redux state from a custom node, it's not possible as it's not updated because we are using useMemo.
  //As a solution, using the ReactFlowProvider and useNodes/useEdges hooks inside custom nodes we have access to current nodes and edges,
  //so we can forward them in any function (eg. onEdit)
  const nodeTypes = useMemo(
    () => ({
      [FLOW_CHART_NODE_TYPES.QUESTION]: (props) => (
        <QuestionCard
          {...props}
          toggleChildren={toggleChildren}
          onEdit={onEdit}
        />
      ),
      [FLOW_CHART_NODE_TYPES.ANSWER]: (props) => (
        <AnswerCard {...props} {...childNodeProps} />
      ),
      [FLOW_CHART_NODE_TYPES.CHAT]: (props) => (
        <ChatCard {...props} toggleChildren={toggleChildren} onEdit={onEdit} />
      ),
      [FLOW_CHART_NODE_TYPES.BUTTON]: (props) => (
        <ButtonCard
          {...props}
          toggleChildren={toggleChildren}
          onEdit={onEdit}
        />
      ),
      [FLOW_CHART_NODE_TYPES.CUSTOM]: (props) => (
        <CustomNodeCard
          {...props}
          {...nodeProps}
          onEdit={onEdit}
          toggleChildren={toggleChildren}
        />
      ),
    }),
    []
  );

  const toggleChildren = (parentId) => {
    dispatch(
      flowBuildersActions.toggleChildren({
        name,
        parentId,
      })
    );
    setMustFitView(true);
  };

  useEffect(() => {
    if (mustFitView) {
      setTimeout(
        () =>
          reactFlowInstance.fitView({
            minZoom: 0.8,
            maxZoom: 2,
            duration: 500,
          }),
        10
      );
      setMustFitView(false);
    }
  }, [mustFitView]);

  useEffect(() => {
    if (reactFlowInstance) {
      let _nodes = flowBuilder.nodes;
      let _edges = flowBuilder.edges;

      let hiddenNodes = _nodes.filter((a) => a.hidden);
      let hiddenEdges = _edges.filter((a) => a.hidden);

      const layouted = getLayoutedElements(
        _nodes.filter((a) => !a.hidden),
        _edges.filter((a) => !a.hidden)
      );

      hiddenNodes.forEach((a) => {
        layouted.nodes.push(a);
      });
      hiddenEdges.forEach((a) => {
        layouted.edges.push(a);
      });

      dispatch(
        flowBuildersActions.update({
          name,
          nodes: layouted.nodes,
          edges: layouted.edges,
        })
      );

      setMustFitView(true);
      setInitialized(true);
    }
  }, [reactFlowInstance]);

  return (
    <ReactFlowProvider>
      <ReactFlow
        onInit={(reactFlowInstance) => {
          setReactFlowInstance(reactFlowInstance);
        }}
        draggable
        nodes={initialized ? flowBuilder.nodes : []}
        edges={initialized ? flowBuilder.edges : []}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        style={style}
        proOptions={proOptions}
      >
        <Controls showInteractive={false} />
      </ReactFlow>
    </ReactFlowProvider>
  );
};

export default LayoutFlow;
